/**
 * FeedbackProvider - Centralized feedback configuration for molecular elements
 *
 * Provides a shared FeedbackManager instance to all child components,
 * enabling consistent visual feedback across the application.
 *
 * External element states (error, success, etc.) are stored as React state
 * so changes are natively reactive — no version counters or forceUpdate needed.
 */

import React, { createContext, useContext, useCallback, useMemo, useState, useEffect } from 'react';
import { FeedbackManager } from './FeedbackManager';
import type { FeedbackConfig, FeedbackState } from './FeedbackManager';

interface FeedbackContextValue {
  manager: FeedbackManager;
  /** External element states stored as React state for reactivity */
  externalStates: Map<string, FeedbackState>;
  /**
   * Set the feedback state for a specific element ID
   * This allows external control (e.g., setting 'error' state for bad bonds)
   */
  setState: (elementId: string, state: FeedbackState) => void;
  /**
   * Get the current feedback state for an element
   */
  getState: (elementId: string) => FeedbackState;
  /**
   * Clear all element states (useful when workspace changes)
   */
  clearStates: () => void;
  /**
   * Global flag to hide all hitboxes (e.g., during rotation drag)
   * When true, all hitboxes should be hidden/disabled
   */
  hideAllHitboxes: boolean;
}

const FeedbackContext = createContext<FeedbackContextValue | null>(null);

export interface FeedbackProviderProps {
  children: React.ReactNode;
  /**
   * Optional feedback configuration
   * If not provided, uses default configuration
   */
  config?: Partial<FeedbackConfig>;
}

export function FeedbackProvider({ children, config }: FeedbackProviderProps) {
  const [manager] = useState(() => new FeedbackManager(config));
  const [externalStates, setExternalStates] = useState<Map<string, FeedbackState>>(() => new Map());
  const [hideAllHitboxes, setHideAllHitboxes] = useState(false);

  const setState = useCallback((elementId: string, state: FeedbackState) => {
    setExternalStates(prev => {
      // Skip update if value hasn't changed
      if (prev.get(elementId) === state) return prev;
      const next = new Map(prev);
      next.set(elementId, state);
      return next;
    });
  }, []);

  const getState = useCallback((elementId: string): FeedbackState => {
    return externalStates.get(elementId) ?? 'idle';
  }, [externalStates]);

  const clearStates = useCallback(() => {
    setExternalStates(new Map());
  }, []);

  // Auto-detect anchor-orbit-dragging state to hide all hitboxes
  useEffect(() => {
    const hasOrbitDragging = Array.from(externalStates.values()).some(
      (state) => state === 'anchor-orbit-dragging'
    );
    setHideAllHitboxes(hasOrbitDragging);
  }, [externalStates]);

  const contextValue = useMemo<FeedbackContextValue>(() => ({
    manager,
    externalStates,
    setState,
    getState,
    clearStates,
    hideAllHitboxes,
  }), [manager, externalStates, setState, getState, clearStates, hideAllHitboxes]);

  return (
    <FeedbackContext.Provider value={contextValue}>
      {children}
    </FeedbackContext.Provider>
  );
}

/**
 * Hook to access the shared FeedbackManager
 * Can be used optionally (returns null if no provider exists)
 */
export function useFeedbackContext(options?: { optional?: boolean }): FeedbackContextValue | null {
  const context = useContext(FeedbackContext);

  if (!context && !options?.optional) {
    throw new Error('useFeedbackContext must be used within a FeedbackProvider');
  }

  return context;
}

/**
 * Enhanced useFeedback hook that uses shared FeedbackManager from context
 * Falls back to creating a local manager if no provider exists (backwards compatible)
 *
 * State priority (highest to lowest):
 * 1. External states from context (error, success, preview, ghost) - set by parent components
 * 2. Internal compound states (hover, selected, disabled) - set by user interaction
 * 3. Idle state - default state
 */
export function useFeedbackWithContext(elementId: string) {
  const feedbackContext = useFeedbackContext({ optional: true });
  const [localManager] = useState(() => new FeedbackManager());
  const [state, setState] = useState<FeedbackState>('idle');
  const [isHovered, setIsHovered] = useState(false);
  const [isSelected, setIsSelected] = useState(false);
  const [isDisabled, setIsDisabled] = useState(false);

  // Use context manager if available, otherwise use local manager
  const manager = feedbackContext?.manager || localManager;

  // Derive external state from React state — reactive by default
  const externalState = feedbackContext?.externalStates.get(elementId);

  useEffect(() => {
    // External states (error, success, preview, ghost, disabled, anchor-orbit modes) override compound states
    const externalOverrides: FeedbackState[] = ['error', 'success', 'preview', 'ghost', 'disabled', 'anchor-orbit-hover', 'anchor-orbit-dragging', 'anchor-orbit-orbiting'];
    if (externalState && externalOverrides.includes(externalState)) {
      setState(externalState);
      manager.setState(elementId, externalState);
      return;
    }

    // Otherwise, use compound state from internal hover/selected/disabled
    const newState = manager.getCompoundState(isHovered, isSelected, isDisabled);
    setState(newState);
    manager.setState(elementId, newState);
  }, [isHovered, isSelected, isDisabled, elementId, manager, externalState]);

  const variants = useMemo(() => manager.createVariants(elementId), [elementId, manager]);
  const currentStyle = manager.getStyle(state);

  // Stable return reference — effects in consumers depend on `feedback` and re-fire
  // every render if this object is recreated, which can push React past its 50-update
  // guard during interactions (drag, hover cascades).
  return useMemo(() => ({
    state,
    variants,
    currentStyle,
    config: manager.getConfig(),
    setHovered: setIsHovered,
    setSelected: setIsSelected,
    setDisabled: setIsDisabled,
    pulse: () => manager.pulseEffect(elementId),
    shake: () => manager.shakeEffect(elementId),
    context: feedbackContext,
  }), [state, variants, currentStyle, manager, elementId, feedbackContext]);
}